import { AbcOutlined, Add, Check, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  debounce,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridLoadingOverlay,
  deDE,
} from '@mui/x-data-grid';
import React, { useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import {
  ILegalClient,
  ILegalClientFullObject,
} from '../Interfaces/ILegalClient';
import { AddFromArnotopDialog } from './Arnotop/AddFromArnotopDialog';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { ClientDasboard } from './ClientDashboard/ClientDasboard';
import {
  useGetDocumentRequested,
  useGetDocumentUploaded,
  useGetMetadataLogin,
} from '../hooks/data_login';
import { ClientDasboardButton } from './ClientDashboard/ClientDasboardButton';
import {
  useGetLegalClientLogs,
  useGetLegalClients,
} from '../hooks/reactQuerys/LegalClientQueries';
import { useGetDownloadedDocuments } from '../hooks/reactQuerys/DocumentQueries';
import {
  ClientsSearch,
  getSearchUri,
  ISearchObject,
  legalClientsPossibleSearchOptions,
} from './ClientsSearch';
import {
  useGetLegalClientGroupJoins,
  useGetLegalClientGroups,
} from '../hooks/reactQuerys/LegalClientGroups';
import { ILegalClientGroup } from '../Interfaces/ILegalClientGroup';
import { SearchType } from '../Interfaces/ISearchType';

import { IPagination } from '../Interfaces/IPagination';
import { useGetLoginLastWeek } from '../hooks/reactQuerys/DashboardQueries';

export const LoadingOverlay = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(1px)',
        zIndex: 1,
        height: '100%',
      }}
    >
      <CircularProgress />
    </Box>
  );
};

export const ClientsOverview: React.FC = () => {
  const initialRowsPerPage = 25;

  const navigate = useNavigate();
  const { legalClientId } = useParams();

  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialRowsPerPage);
  const [searchType, setSearchType] = useState(SearchType.INCLUDES);
  //
  const [showedData, setShowedData] = useState<IPagination<ILegalClient[]>>({
    count: 0,
    next: 1,
    previous: null,
    results: [],
  });
  //
  const [search, setSearch] = useState<ISearchObject>({
    searchTerm: '',
    searchFilter: legalClientsPossibleSearchOptions.map(
      item => item.SearchOptionKey
    ),
    groupFilter: [],
    lastLoginFilter: null,
    syncWithArnoTop: null,
    loginPossible: null,
    isReceivingEmailEnabled: null,
    searchType: 'includes',
  });

  const [searchUri, setSearchUri] = useState(
    getSearchUri(page, pageSize, search)
  );
  //
  const [showArnoTopNoSyncDialog, setShowArnoTopNoSyncDialog] = useState(false);
  const [activeDashboard, setActiveDashboard] = useState(() => {
    const settingDashboard = JSON.parse(
      String(localStorage.getItem('client_dashboard'))
    );
    if (settingDashboard === null) {
      const returnArray = [
        'PieChartLoggedUser',
        'PieChartUser',
        'ComposedChartLogin',
        'ComposedDocuments',
      ];
      localStorage.setItem('client_dashboard', JSON.stringify(returnArray));
      return returnArray;
    }
    return settingDashboard;
  });

  const {
    legalClients,
    isLoadingLegalClients,
    refetchLegalClients,
    abortLegalClients,
  } = useGetLegalClients(searchUri);

  const { groupJoin } = useGetLegalClientGroupJoins();
  const { legalClientGroups } = useGetLegalClientGroups();

  useEffect(() => {
    refetchLegalClients(); // trigger manuell, wenn sich route ändert
  }, [legalClientId]);

  const resetByNew = () => {
    if (page !== 0) {
      setPage(0);
      doSearch(0, pageSize, search);
    } else {
      refetchLegalClients();
    }
  };

  const doSearch = (
    currentPage: number,
    currentPageSize: number,
    currentSearchObject: ISearchObject
  ) => {
    abortLegalClients();
    setSearchUri(
      getSearchUri(currentPage, currentPageSize, currentSearchObject)
    );
  };

  const setActiveDashboardWrapper = (setting: string[]) => {
    setActiveDashboard(setting);
    localStorage.setItem('client_dashboard', JSON.stringify(setting));
  };

  const columns: GridColDef[] = [
    { field: 'idLegalClient', headerName: 'Nr.', flex: 0.3 },
    { field: 'idArnoTopIntern', headerName: 'Namen.Nr.', flex: 0.5 },
    {
      field: 'LegalClientGroups',
      headerName: 'Gruppen',
      flex: 1.2,
      sortable: false,
      renderCell: ({ row }) => {
        const mappings = groupJoin.filter(
          j => j.idLegalClient === row.idLegalClient
        );

        const groups = mappings
          .map(m =>
            legalClientGroups.find(
              g => g.idLegalClientGroup === m.idLegalClientGroup
            )
          )
          .filter((g): g is ILegalClientGroup => Boolean(g));

        return (
          <Box sx={{ display: 'flex', gap: 1, overflow: 'hidden' }}>
            {groups.slice(0, 2).map(g => (
              <Chip
                key={g.idLegalClientGroup}
                label={g.Name}
                size='small'
                sx={{
                  backgroundColor: g.ColorHex ?? '#666',
                  color: 'white',
                }}
              />
            ))}
            {groups.length > 2 && (
              <Tooltip
                title={
                  <Box>
                    {groups.map(g => (
                      <Chip
                        key={g.idLegalClientGroup}
                        label={g.Name}
                        size='small'
                        sx={{
                          backgroundColor: g.ColorHex ?? '#666',
                          color: 'white',
                          mb: '4px',
                        }}
                      />
                    ))}
                  </Box>
                }
              >
                <Chip
                  label={`+${groups.length - 2}`}
                  size='small'
                  sx={{
                    backgroundColor: '#777',
                    color: 'white',
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'last_login_at',
      headerName: 'Letzter Login',
      flex: 1,
      renderCell: params =>
        params.value == undefined
          ? '-'
          : new Date(params.value).toLocaleString(),
    },
    {
      field: 'syncWithArnoTop',
      headerName: 'ArnoTop',
      flex: 0.3,
      renderCell: params => (Boolean(params.value) ? <Check /> : <Close />),
    },
    {
      field: 'loginPossible',
      headerName: 'Login',
      flex: 0.3,
      renderCell: params => (Boolean(params.value) ? <Check /> : <Close />),
    },
    {
      field: 'isReceivingEmailEnabled',
      headerName: 'E-Mails',
      flex: 0.3,
      renderCell: params => (Boolean(params.value) ? <Check /> : <Close />),
    },
    { field: 'Email', headerName: 'E-Mail-Adresse', flex: 1 },
    { field: 'FirstName', headerName: 'Vorname', flex: 1 },
    { field: 'LastName', headerName: 'Nachname/Firma', flex: 1 },
  ];

  const debouncedSearch = useMemo(
    () =>
      debounce(
        (
          currentPage: number,
          currentPageSize: number,
          currenSearchObject: ISearchObject
        ) => {
          doSearch(currentPage, currentPageSize, currenSearchObject);
        },
        700
      ),
    []
  );

  useEffect(() => {
    if (legalClients) {
      setShowedData(legalClients);
    }
  }, [legalClients]);

  useEffect(() => {
    debouncedSearch(page, pageSize, search);
  }, [search]);

  return (
    <>
      <Box sx={{ mr: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='h4'>Mandanten</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <ClientDasboardButton
              activeDashboard={activeDashboard}
              setActiveDashboard={setActiveDashboardWrapper}
            />
            <Button
              onClick={() => setShowArnoTopNoSyncDialog(true)}
              variant='outlined'
            >
              Neue über ARNOtop
            </Button>
            <Tooltip title='Neuer Mandant'>
              <IconButton onClick={() => navigate('new')}>
                <Add />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ mt: 6 }}>
          <ClientDasboard
            activeDashboard={activeDashboard}
            setActiveDashboard={setActiveDashboard}
          />
        </Box>

        <ClientsSearch
          groups={legalClientGroups}
          search={search}
          setSearch={setSearch}
        />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DataGrid
              loading={isLoadingLegalClients}
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
              rows={showedData.results}
              columns={columns}
              getRowId={row => row.idLegalClient}
              rowCount={showedData.count}
              pagination
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={model => {
                setPageSize(model.pageSize);
                setPage(model.page);
                debouncedSearch(model.page, model.pageSize, search);
              }}
              paginationMode='server'
              pageSizeOptions={[initialRowsPerPage, 50, 100]}
              slots={{
                loadingOverlay: () => <LoadingOverlay />,
              }}
              autoHeight
              onRowClick={({ row }: { row: ILegalClientFullObject }) =>
                navigate(`${row.idLegalClient}`)
              }
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
